import React from "react";
import { PageComponentProps } from "../../interfaces/common";
import styled from "styled-components";
import {
  pxToEm,
  generateValidTo,
  generateValidFrom,
  hexToRGB
} from "../../util/helpers";
import constants from "../_globalStyles/constants";
import { useTranslation } from "react-i18next";
import ValidateBox from "../organisms/ValidateBox";
import InfoBox from "../molecules/InfoBox";
import Line from "../../assets/icons/line.svg";
import TextViewer from "../organisms/TextViewer";
import Gallery from "../organisms/Gallery";
import { darkColors } from "../../util/ticketColor";

import TicketPlaceholderImg from "../../assets/img/ticket_placeholder.jpg";

import Icon from "../atoms/Icon";

const TicketView = styled.section<{
  color: string;
  opaqueColorDetails?: string;
  isDarkColorOverlay?: boolean;
  opaqueColor?: string;
}>`
  max-width: ${pxToEm(460, 16)}rem;
  margin: auto;

  .ticket-heading {
    background: ${({ color }) => color};
    border-bottom-left-radius: 32px;
    border-bottom-right-radius: 32px;
    ${({ color }) =>
      color === "#ffffff" ? `border: 1px solid rgba(0,0,0,0.08);` : ""}
  }

  .gallery-wrap {
    overflow: hidden;
    border-bottom-left-radius: 32px;
    border-bottom-right-radius: 32px;
  }
  .gallery {
    position: relative;
    flex-basis: 100%;
    flex-shrink: 0;
  }

  .top-logo {
    z-index: 10;
    height: 60px;
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 12px 24px;
    border-radius: 120px;
  }

  .slide-item {
    height: 100%;
    width: 100%;
    img,
    object {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .shadow {
    display: block;
    pointer-events: none;
    height: 128px;
    width: 100%;
    position: absolute;
    bottom: -2px;
    left: 0;
    z-index: 1;
    background: linear-gradient(
      180deg,
      rgba(255, 209, 109, 0) 0%,
      ${({ color }) => color} 69.58%
    );
  }

  .title {
    width: 100%;
    position: relative;
    padding: 48px 24px 24px;

    h1 {
      font-size: 24px;
      font-weight: bold;
      letter-spacing: 0.72px;
      margin: 0;
      color: ${({ isDarkColorOverlay }) =>
        isDarkColorOverlay ? "white" : "black"};
    }
  }

  .badges {
    z-index: 3;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    padding-left: 24px;
    padding-right: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .provider {
      padding: 4px 16px;
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(25px);
      border-radius: 40px;
      min-height: 56px;
      display: flex;
      align-items: center;
      border: 4px solid ${({ opaqueColor }) => opaqueColor};
      max-width: 50%;

      p {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        font-size: 16px;
        margin: 0;
      }
    }

    .badge {
      background: ${constants.OK_ORANGE};
      padding: 4px 12px;
      border-radius: 24px;

      p {
        font-size: 16px;
        margin: 0;
        font-weight: bold;
        color: white;
      }
    }
  }

  .details-wrap {
    position: relative;
    z-index: -1;
    padding-top: 40px;
    padding-bottom: 16px;
    border-radius: 24px;
    margin: -24px 16px 16px;
    background: ${({ opaqueColorDetails }) => opaqueColorDetails};
    ${({ color }) =>
      color === "#ffffff" ? `border: 1px solid rgba(0,0,0,0.08);` : ""}
  }

  .details {
    margin: 0;
    list-style-type: none;
    padding: 0;
    text-align: center;

    li {
      padding: 16px 0;
      position: relative;

      + li:before {
        content: "";
        height: 6px;
        background: no-repeat url(${Line});
        width: 124px;
        position: absolute;
        top: -3px;
        left: 50%;
        transform: translateX(-50%);
      }
    }

    span {
      display: block;
      font-size: 14px;
      margin-bottom: 4px;
      leading: 20px;
      color: ${constants.TEXT_LIGHT};
    }

    p {
      margin: 0;
      font-weight: bold;
      font-size: 18px;
      leading: 20px;
      letter-spacing: 0.54px;
    }
  }

  .description {
    background: ${({ opaqueColorDetails }) => opaqueColorDetails};
    padding: 24px;
    margin: 16px;
    border-radius: 24px;

    p {
      margin: 0;
    }

    p + p {
      margin: 8px 0 0;
    }
  }

  .link {
    margin: 0 1rem ${pxToEm(40, 16)}rem;
    font-size: ${pxToEm(18, 16)}rem;
    display: inline-block;
  }

  .info-box {
    margin: 16px;
  }
`;

interface TicketProps extends PageComponentProps {
  ticketDetails?: {
    serviceProvider: string | null;
    title: string;
    canBeUsedBy: string;
    validTo: false | "" | Date | null;
    validFrom: false | "" | Date | null;
    description: string | null;
    linkUrl: string | null;
    entryLimit: number | null;
    timesUsed: number | null;
    minimumAge: number | null;
    code: string | null;
    discount: string | null;
    type?: string;
    overlayColor: string | null;
    images: string[] | null;
  };
  myMunicipality: string | boolean;
  handleValidateTicket: () => void;
}

const Ticket: React.FunctionComponent<TicketProps> = ({
  ticketDetails,
  myMunicipality,
  handleValidateTicket
}) => {
  const [t] = useTranslation();

  const opaqueColor: string = ticketDetails?.overlayColor
    ? hexToRGB(ticketDetails.overlayColor || "#ffffff", 0.6)
    : "rgba(0,0,0,0.08)";
  const opaqueColorDetails: string = hexToRGB(
    ticketDetails?.overlayColor || "#ffffff",
    0.2
  );

  const isDarkColorOverlay = Boolean(
    ticketDetails?.overlayColor &&
      darkColors.includes(ticketDetails.overlayColor)
  );

  const validTo =
    ticketDetails &&
    ticketDetails.validTo &&
    generateValidTo(ticketDetails?.validTo);

  const validFrom =
    ticketDetails &&
    ticketDetails.validFrom &&
    generateValidFrom(ticketDetails.validFrom);

  const ticketDateStarted =
    ticketDetails &&
    ticketDetails.validFrom &&
    ticketDetails.validFrom <= new Date();

  const unlimitedEntries = ticketDetails && ticketDetails.entryLimit === null;

  const availiableEntries =
    ticketDetails &&
    ticketDetails.entryLimit &&
    (ticketDetails.timesUsed || ticketDetails.timesUsed === 0) &&
    ticketDetails.entryLimit - ticketDetails.timesUsed;

  const images = ticketDetails?.images || [TicketPlaceholderImg]; // [img1, img2, img3, img4, img5];

  return (
    <TicketView
      color={ticketDetails?.overlayColor || "#ffffff"}
      opaqueColorDetails={opaqueColorDetails}
      opaqueColor={opaqueColor}
      isDarkColorOverlay={isDarkColorOverlay}
    >
      <div className="ticket-heading">
        <div className="gallery">
          <Icon className="top-logo" name="logo" />
          <div className="gallery-wrap">
            <Gallery
              slides={images.map((img, index) => (
                <div key={index} className="slide-item">
                  <object data={img} type="image/jpg">
                    <img src={TicketPlaceholderImg} />
                  </object>
                </div>
              ))}
            />
          </div>
          <div className="shadow"></div>
        </div>

        <div className="title">
          <div className="badges">
            <div className="provider">
              <p>{ticketDetails?.serviceProvider}</p>
            </div>
            {ticketDetails?.discount && (
              <div className="badge">
                <p>{ticketDetails?.discount}</p>
              </div>
            )}
          </div>
          <h1>{ticketDetails?.title}</h1>
        </div>
      </div>

      <div className="details-wrap">
        <ul className="details">
          <li>
            <span className="cfl">{t("general.valid_for")}</span>
            <p className="cfl">
              {t(`general.who_can_use.${ticketDetails?.canBeUsedBy}`)}
            </p>
          </li>
          {ticketDetails?.minimumAge && (
            <li>
              <span className="cfl">{t("general.minimum_age")}</span>
              <p>{ticketDetails?.minimumAge}</p>
            </li>
          )}

          {ticketDateStarted && ticketDetails?.validTo !== null && (
            <li>
              <span className="cfl">{t("general.valid_to")}</span>
              <p>{validTo}</p>
            </li>
          )}
          {!ticketDateStarted && (
            <li>
              <span className="cfl">{t("general.valid")}</span>
              <p>
                {ticketDetails?.validTo === null
                  ? `${validFrom} - ∞`
                  : `${validFrom} - ${validTo}`}
              </p>
            </li>
          )}
          {ticketDetails?.entryLimit && ticketDetails.entryLimit !== 1 && (
            <li>
              <span className="cfl">{t("general.used")}</span>
              <p>
                {ticketDetails &&
                  `${ticketDetails.timesUsed} ${t("general.of")} ${
                    ticketDetails.entryLimit
                  }`}
              </p>
            </li>
          )}
        </ul>
      </div>

      {(availiableEntries || availiableEntries === null) &&
        ticketDateStarted && (
          <ValidateBox
            idPrefix="validation-box"
            unlimitedEntries={unlimitedEntries}
            serviceProvider={ticketDetails?.serviceProvider}
            myMunicipality={myMunicipality}
            handleValidateTicket={handleValidateTicket}
            availiableEntries={availiableEntries}
            code={ticketDetails?.code}
            ticketType={ticketDetails?.type}
          />
        )}
      {!ticketDateStarted && (
        <InfoBox
          className="info-box"
          idPrefix="ticket-used-out"
          emoji="⏰"
          text={t("general.not_started")}
          emojiSize="small"
        />
      )}
      {!availiableEntries && !unlimitedEntries && (
        <InfoBox
          className="info-box"
          idPrefix="ticket-used-out"
          emoji="🎯"
          text={t("general.used_out")}
          emojiSize="small"
        />
      )}
      {ticketDetails?.description && (
        <div className="description">
          <div>
            <TextViewer data={ticketDetails?.description} />
          </div>
        </div>
      )}
      {ticketDetails?.linkUrl && (
        <a className="link cfl" href={ticketDetails?.linkUrl} target="blank">
          {t("general.details_link")}
        </a>
      )}
    </TicketView>
  );
};

export default Ticket;
